import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import Pagination from "../pagination/pagination";
import { CellData } from "./table";
import TableBody from "./tableBody";

interface TableWithPaginationProps {
  tableName: string;
  sortedTableHeaders: any[];
  computedCellData: CellData[][];
  tableDataToRender: any[];
  tableWidth: number;
  onSortTableData: (key: string, subKey?: string, type?: string) => void;
  onRowClick: (tableData: any[], rowIndex: number, className: string) => void;
  onAssignToClick?: (data: any) => void;
  onStatusClick?: (data: any) => void;
  onDeleteIconClick?: (data: any) => void;
  onEditIconClick?: (data: any) => void;
  onIconCellClick?: (data: any) => void;
  hidePagination?: boolean;
  count?: number;
  itemsPerPage?: number;
  pagesPerView?: number;
  handlePageChange: (startIndex: number, endIndex: number, data: any[]) => void;
  triggerHandlePageChange: number;
  paginationData: React.ReactNode;
  onClickableTextWithDisabledStatusClick?: (data: any) => void;
  showInLineLoader?: boolean;
  onComplete?: () => void;
  /** Fired when a CLICKABLE_BADGE_WITH_ON_CLICK cell is clicked; receives the cell `id`. */
  onClickableBadgeClick?: (id: string) => void;
  onSelectAll?: () => void;
  toggleCheckboxSelection?: (id: string) => void;
  handleDownloadButtonClick?: (id: string) => void;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  startIndex?: number;
  isPreviousButtonDisabled?: boolean;
  onSideModalClose?: () => void;
  onChangeEmit?: (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => void;
  onPropagateEventOnClickInRpaStatusCell?: (id: string) => void;
  onPropagateEventOnClickInRpaStatusTwoCell?: (id: string) => void;
  onRpaStatusForExternalClick?: (
    modalType: "image" | "doc",
    orderId: string,
  ) => void;
  onValueChangeOfTextField?: (value: string, id: string, type?: string) => void;
  onOverlappingIconClick?: (icon_id: string, order_id: string) => void;
  rowClassName?: string | ((rowData: any, rowIndex: number) => string);
  onUploadClick?: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  onSuccessClick?: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  handleIconClickOnDocumentScreenshot?: (rowData: any) => void;
  /** Fired when the user types in a SEARCHABLE_CARD cell's search input. `id` is the cell value's id. */
  onSearchChange?: (searchTerm: string, id: string) => void;
  /** Fired when the user creates a new option in a SEARCHABLE_CARD cell. `id` is the cell value's id. */
  onCreateOption?: (value: string, id: string) => void;
  /** Fired when the user clicks an option in a SEARCHABLE_CARD cell. `id` is the cell value's id. */
  onOptionClick?: (option: SearchableCardOption, id: string) => void;
}

const TableWithPagination: React.FC<TableWithPaginationProps> = (props) => {
  return (
    <>
      <div className="table">
        <table key={props.tableName}>
          <TableBody
            computedCellData={props.computedCellData}
            tableDataToRender={props.tableDataToRender}
            tableWidth={props.tableWidth}
            onRowClick={props.onRowClick}
            onAssignToClick={props.onAssignToClick}
            onStatusClick={props.onStatusClick}
            onDeleteIconClick={props.onDeleteIconClick}
            onEditIconClick={props.onEditIconClick}
            onIconCellClick={props.onIconCellClick}
            onClickableTextWithDisabledStatusClick={
              props.onClickableTextWithDisabledStatusClick
            }
            onComplete={props.onComplete}
            onClickableBadgeClick={props.onClickableBadgeClick}
            toggleCheckboxSelection={props.toggleCheckboxSelection}
            handleDownloadButtonClick={props.handleDownloadButtonClick}
            onTableRowUpdate={props.onTableRowUpdate}
            startIndex={props.startIndex}
            onSideModalClose={props.onSideModalClose}
            onChangeEmit={props.onChangeEmit}
            onPropagateEventOnClickInRpaStatusCell={
              props.onPropagateEventOnClickInRpaStatusCell
            }
            onPropagateEventOnClickInRpaStatusTwoCell={
              props.onPropagateEventOnClickInRpaStatusTwoCell
            }
            onRpaStatusForExternalClick={props.onRpaStatusForExternalClick}
            onValueChangeOfTextField={props.onValueChangeOfTextField}
            onOverlappingIconClick={props.onOverlappingIconClick}
            rowClassName={props.rowClassName}
            onUploadClick={props.onUploadClick}
            onSuccessClick={props.onSuccessClick}
            handleIconClickOnDocumentScreenshot={
              props.handleIconClickOnDocumentScreenshot
            }
            onSearchChange={props.onSearchChange}
            onCreateOption={props.onCreateOption}
            onOptionClick={props.onOptionClick}
          />
        </table>
      </div>
      {props.showInLineLoader && (
        <div className="table-loader mb-4 mr-4 flex items-center justify-end gap-2 text-small font-regular">
          <SpinningLoader />
          Loading Data ...
        </div>
      )}
      {!props.hidePagination && (
        <div className="table-pagination mt-4">
          {(props.count ?? 0) > 0 && (
            <>
              <Pagination
                itemsCount={props.count ?? 0}
                itemsPerPage={props.itemsPerPage ?? 0}
                pagesPerView={props.pagesPerView ?? 0}
                sendStartAndEndIndex={props.handlePageChange}
                triggerHandlePageChange={props.triggerHandlePageChange}
                tableName={props.tableName}
                isPreviousButtonDisabled={props.isPreviousButtonDisabled}
              />
              <div className="pagination-details">{props.paginationData}</div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default TableWithPagination;
